/**
 * RENDA-K のトグルスイッチ（QUANTIZE 等の ON-OFF 用）。
 *
 * スプラ風のインクピル型。グローバルの Toggle はフォーム向けの
 * ライトテーマ pill で、トイガンパネル上の装飾的スイッチとは用途・
 * 見た目が別物のため、ページ局所で実装する（soremo-fe-rules C-3）。
 */

import React from "react";
import styled from "styled-components";

import { RENDA_FONT, RENDA_PANEL } from "../palette";

const Root = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  user-select: none;

  &:focus-visible {
    outline: 2px solid ${RENDA_PANEL.focus};
    outline-offset: 4px;
    border-radius: 4px;
  }
`;

const Label = styled.span`
  font-family: ${RENDA_FONT};
  font-weight: 800;
  font-size: 11px;
  letter-spacing: 0.12em;
  color: ${RENDA_PANEL.engraving};
  text-transform: uppercase;
`;

const Track = styled.span<{ $on: boolean; $color: string }>`
  width: 52px;
  height: 26px;
  border-radius: 999px;
  border: 3px solid ${RENDA_PANEL.outline};
  background: ${({ $on, $color }) => ($on ? $color : "#d8d2bc")};
  box-shadow: inset 0 2px 3px rgba(0, 0, 0, 0.25);
  display: block;
  position: relative;
  transition: background 0.15s ease-out;
`;

const InkDrop = styled.span<{ $on: boolean }>`
  position: absolute;
  top: 1px;
  left: ${({ $on }) => ($on ? "27px" : "1px")};
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: radial-gradient(
    circle at 35% 30%,
    #ffffff 0%,
    #f2ecd6 60%,
    #d8d2bc 100%
  );
  border: 2px solid ${RENDA_PANEL.outline};
  box-sizing: border-box;
  transition: left 0.15s ease-out;
`;

const StateText = styled.span<{ $on: boolean }>`
  font-family: ${RENDA_FONT};
  font-weight: 800;
  font-size: 10px;
  letter-spacing: 0.14em;
  color: ${({ $on }) => ($on ? RENDA_PANEL.engravingStrong : RENDA_PANEL.tick)};
`;

export interface ToggleSwitchProps {
  label: string;
  value: boolean;
  onChange: (value: boolean) => void;
  color: string;
}

const ToggleSwitch: React.FC<ToggleSwitchProps> = ({
  label,
  value,
  onChange,
  color,
}) => (
  <Root
    type="button"
    role="switch"
    aria-checked={value}
    aria-label={label}
    onClick={() => onChange(!value)}
  >
    <Label>{label}</Label>
    <Track $on={value} $color={color}>
      <InkDrop $on={value} />
    </Track>
    <StateText $on={value}>{value ? "ON" : "OFF"}</StateText>
  </Root>
);

export default ToggleSwitch;
